/* global navigator, document */

/**
 * Copy plain text to the clipboard, robustly.
 *
 * Inside Outlook's add-in iframe `navigator.clipboard.writeText` frequently
 * rejects (permissions policy / not a trusted gesture). When that happens the
 * clipboard silently keeps its previous contents — which is why "Copy" used to
 * paste a stale image instead of the rewrite. We try the async API first, then
 * fall back to a hidden <textarea> + execCommand("copy"), and finally report
 * failure so the caller can tell the user instead of pretending it worked.
 *
 * Returns true only when the text was actually placed on the clipboard.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path below */
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
